import { useState } from 'react';
import {
  CATEGORIES,
  CONTENT_CATEGORIES,
  DIFFICULTIES,
  DISPLAY_TYPES,
  MUSCLE_KEYS,
  MUSCLES,
} from './constants';
import { createExploreItem, generateExploreItem, updateExploreItem } from './api';
import MediaField from './MediaField';

// ─── Helpers ────────────────────────────────────────────────────────

const linesToList = (text) =>
  text.split('\n').map((s) => s.trim()).filter(Boolean);

const listToLines = (list) => (list ?? []).join('\n');

const initialState = (item, initialCategory) => ({
  category: item ? item.category : initialCategory,
  title: item?.title ?? '',
  description: item?.description ?? '',
  thumbnailUrl: item?.thumbnailUrl ?? '',
  thumbnailPublicId: item?.thumbnailPublicId ?? '',
  spanX: item?.spanX ?? 1,
  spanY: item?.spanY ?? 1,
  order: item?.order ?? 0,
  displayType: item?.displayType ?? 'image',
  isActive: item?.isActive ?? true,
  isPremium: item?.isPremium ?? false,

  // Recipes
  calories: item?.calories?.toString() ?? '',
  prepTime: item?.prepTime ?? '',
  ingredients: listToLines(item?.ingredients),
  steps: listToLines(item?.steps),
  protein: item?.macros?.protein?.toString() ?? '',
  carb: item?.macros?.carb?.toString() ?? '',
  fat: item?.macros?.fat?.toString() ?? '',
  difficulty: item?.difficulty ?? null,

  // Exercise videos
  videoUrl: item?.videoUrl ?? '',
  videoPublicId: item?.videoPublicId ?? '',
  duration: item?.duration ?? '',
  targetMuscles: item?.targetMuscles ?? [],
  instructions: listToLines(item?.instructions),

  // Content categories
  content: item?.content ?? '',
  readTime: item?.readTime ?? '',
  tags: (item?.tags ?? []).join(', '),
  rating: item?.rating?.toString() ?? '',
  ratingCount: item?.ratingCount?.toString() ?? '',
  learningObjectives: listToLines(item?.learningObjectives),
});

/** Mirrors _buildBody() in admin_form_screen.dart — only non-empty fields are sent. */
export function buildBody(form) {
  const body = {
    title: form.title.trim(),
    category: form.category,
    spanX: form.spanX,
    spanY: form.spanY,
    displayType: form.displayType,
    order: form.order,
    isActive: form.isActive,
    isPremium: form.isPremium,
  };

  if (form.description.trim()) body.description = form.description.trim();
  // URL ve publicId birlikte, KOSULSUZ gonderilir. Yalnizca publicId'yi
  // gondermek ikisini birbirinden ayirirdi: admin URL kutusunu bosaltinca
  // PATCH govdesinde publicId:'' olurken thumbnailUrl anahtari hic olmaz,
  // backend eski URL'i tutar ve gostermeye devam ettigi dosyayi silerdi.
  // Bos string de anlamli bir deger: "bu alan temizlendi".
  body.thumbnailUrl = form.thumbnailUrl.trim();
  body.thumbnailPublicId = form.thumbnailPublicId;

  if (form.category === 'recipes') {
    const calories = parseInt(form.calories, 10);
    if (!Number.isNaN(calories)) body.calories = calories;
    if (form.prepTime.trim()) body.prepTime = form.prepTime.trim();
    if (form.ingredients.trim()) body.ingredients = linesToList(form.ingredients);
    if (form.steps.trim()) body.steps = linesToList(form.steps);

    const protein = parseInt(form.protein, 10);
    const carb = parseInt(form.carb, 10);
    const fat = parseInt(form.fat, 10);
    if (![protein, carb, fat].every(Number.isNaN)) {
      body.macros = {
        ...(Number.isNaN(protein) ? {} : { protein }),
        ...(Number.isNaN(carb) ? {} : { carb }),
        ...(Number.isNaN(fat) ? {} : { fat }),
      };
    }
    if (form.difficulty) body.difficulty = form.difficulty;
  } else if (form.category === 'exercise_videos') {
    // thumbnailUrl ile ayni gerekce: URL ve publicId ayrilmamali.
    body.videoUrl = form.videoUrl.trim();
    body.videoPublicId = form.videoPublicId;
    if (form.duration.trim()) body.duration = form.duration.trim();
    body.targetMuscles = form.targetMuscles;
    if (form.instructions.trim()) body.instructions = linesToList(form.instructions);
    if (form.difficulty) body.difficulty = form.difficulty;
  } else {
    if (form.content.trim()) body.content = form.content.trim();
    if (form.readTime.trim()) body.readTime = form.readTime.trim();
    if (form.tags.trim()) {
      body.tags = form.tags.split(',').map((s) => s.trim()).filter(Boolean);
    }
    const rating = parseFloat(form.rating);
    if (!Number.isNaN(rating)) body.rating = rating;
    const ratingCount = parseInt(form.ratingCount, 10);
    if (!Number.isNaN(ratingCount)) body.ratingCount = ratingCount;
    if (form.learningObjectives.trim()) {
      body.learningObjectives = linesToList(form.learningObjectives);
    }
  }

  return body;
}

/** Mirrors _fillFromGenerated() — merges AI output into the current form. */
function applyGenerated(form, gen) {
  const next = {
    ...form,
    title: gen.title ?? form.title,
    description: gen.description ?? form.description,
  };

  if (form.category === 'recipes') {
    if (gen.calories != null) next.calories = String(gen.calories);
    if (gen.prepTime != null) next.prepTime = gen.prepTime;
    if (gen.ingredients) next.ingredients = gen.ingredients.join('\n');
    if (gen.steps) next.steps = gen.steps.join('\n');
    if (gen.macros) {
      if (gen.macros.protein != null) next.protein = String(gen.macros.protein);
      if (gen.macros.carb != null) next.carb = String(gen.macros.carb);
      if (gen.macros.fat != null) next.fat = String(gen.macros.fat);
    }
    if (gen.difficulty) next.difficulty = gen.difficulty;
  } else if (form.category === 'exercise_videos') {
    if (gen.duration != null) next.duration = gen.duration;
    if (gen.targetMuscles) {
      next.targetMuscles = gen.targetMuscles
        .map(String)
        .filter((m) => MUSCLE_KEYS.includes(m));
    }
    if (gen.instructions) next.instructions = gen.instructions.join('\n');
    if (gen.difficulty) next.difficulty = gen.difficulty;
  } else {
    if (gen.content != null) next.content = gen.content;
    if (gen.readTime != null) next.readTime = gen.readTime;
    if (gen.tags) next.tags = gen.tags.join(', ');
  }

  return next;
}

// ─── Shared inputs ──────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <h2 className="text-dcp-primary font-bold text-[15px] pt-1 pb-3">{children}</h2>
);

const LabeledField = ({ label, hint, children }) => (
  <div className="pb-1">
    <div className="flex items-baseline gap-2">
      <span className="text-dcp-text font-semibold text-[13px]">{label}</span>
      <span className="text-dcp-text-3 text-[11px]">{hint}</span>
    </div>
    <div className="mt-2">{children}</div>
  </div>
);

const TextField = ({ label, value, onChange, rows = 1, type = 'text', required = false }) => {
  const shared =
    'w-full bg-dcp-surface border border-dcp-border rounded-xl px-3.5 py-3 text-sm ' +
    'text-dcp-text placeholder-dcp-text-2 outline-none focus:border-dcp-primary transition-colors';

  return (
    <label className="block pb-3">
      <span className="block text-dcp-text-2 text-[13px] mb-1.5">
        {label}
        {required && <span className="text-dcp-error"> *</span>}
      </span>
      {rows > 1 ? (
        <textarea
          className={`${shared} resize-y`}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className={shared}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
};

const SwitchTile = ({ label, hint, value, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className={`flex-1 flex items-center gap-2 text-left px-3 py-2.5 rounded-xl border transition-colors bg-dcp-surface ${
      value ? 'border-dcp-primary/40' : 'border-dcp-border'
    }`}
  >
    <div className="flex-1 min-w-0">
      <p className={`font-semibold text-xs ${value ? 'text-dcp-text' : 'text-dcp-text-2'}`}>
        {label}
      </p>
      <p className="text-dcp-text-3 text-[9px] truncate">{hint}</p>
    </div>
    <span
      className={`shrink-0 w-9 h-5 rounded-full p-0.5 transition-colors ${
        value ? 'bg-dcp-primary' : 'bg-dcp-surface-variant'
      }`}
    >
      <span
        className={`block w-4 h-4 rounded-full bg-white transition-transform ${
          value ? 'translate-x-4' : ''
        }`}
      />
    </span>
  </button>
);

const Segmented = ({ options, value, onChange }) => (
  <div className="flex gap-2">
    {options.map((option) => (
      <button
        key={option}
        type="button"
        onClick={() => onChange(option)}
        className={`flex-1 py-2 rounded-[10px] border text-sm font-semibold transition-colors ${
          value === option
            ? 'bg-dcp-primary border-dcp-primary text-dcp-on-primary'
            : 'bg-dcp-surface border-dcp-border text-dcp-text-2 hover:border-dcp-border-dark'
        }`}
      >
        {option}
      </button>
    ))}
  </div>
);

// ─── Screen ─────────────────────────────────────────────────────────

export default function AdminForm({ item, initialCategory, onClose, onSaved }) {
  const isEdit = Boolean(item);
  const [form, setForm] = useState(() => initialState(item, initialCategory));
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleMuscle = (key) =>
    setForm((prev) => ({
      ...prev,
      targetMuscles: prev.targetMuscles.includes(key)
        ? prev.targetMuscles.filter((m) => m !== key)
        : [...prev.targetMuscles, key],
    }));

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setError('Başlık zorunlu.');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const body = buildBody(form);
      if (isEdit) {
        await updateExploreItem(item._id, body);
      } else {
        await createExploreItem(body);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
      setIsSaving(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const generated = await generateExploreItem(form.category);
      setForm((prev) => applyGenerated(prev, generated));
    } catch (err) {
      setError(err.message);
    }
    setIsGenerating(false);
  };

  return (
    <form onSubmit={handleSave} className="min-h-screen bg-dcp-bg">
      {/* App bar */}
      <header className="sticky top-0 z-10 bg-dcp-bg border-b border-dcp-border">
        <div className="max-w-2xl mx-auto flex items-center gap-3 px-4 h-16">
          <button
            type="button"
            onClick={onClose}
            className="text-dcp-text text-2xl leading-none w-8 h-8 rounded-full hover:bg-dcp-surface"
            aria-label="Kapat"
          >
            ×
          </button>
          <h1 className="flex-1 text-dcp-text font-bold text-lg">
            {isEdit ? 'Düzenle' : 'Yeni İçerik'}
          </h1>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="text-dcp-primary font-semibold text-sm px-3 py-1.5 rounded-lg hover:bg-dcp-surface disabled:opacity-50"
          >
            {isGenerating ? 'Üretiliyor…' : '✦ AI ile Doldur'}
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 pb-16">
        {error && (
          <div className="mb-4 rounded-xl border border-dcp-error/40 bg-dcp-error/10 px-4 py-3 text-sm text-dcp-error">
            {error}
          </div>
        )}

        {/* Kategori — locked while editing, same as the mobile dropdown */}
        <label className="block pb-4">
          <span className="block text-dcp-text-2 text-[13px] mb-1.5">Kategori</span>
          <select
            value={form.category}
            onChange={(e) => set('category')(e.target.value)}
            disabled={isEdit}
            className="w-full bg-dcp-surface border border-dcp-border rounded-xl px-3.5 py-3 text-sm text-dcp-text outline-none focus:border-dcp-primary disabled:opacity-60"
          >
            {CATEGORIES.map((cat) => (
              <option key={cat.key} value={cat.key}>
                {cat.label}
              </option>
            ))}
          </select>
        </label>

        <SectionLabel>Temel Bilgiler</SectionLabel>
        <TextField label="Başlık" value={form.title} onChange={set('title')} required />
        <TextField label="Açıklama" value={form.description} onChange={set('description')} rows={2} />

        <SectionLabel>Görünüm</SectionLabel>
        <LabeledField label="Görünüm Tipi" hint="Kartın nasıl görüntüleneceği">
          <div className="grid grid-cols-2 gap-2">
            {DISPLAY_TYPES.map((dt) => {
              const selected = dt.key === form.displayType;
              return (
                <button
                  key={dt.key}
                  type="button"
                  onClick={() => set('displayType')(dt.key)}
                  className={`text-left px-3 py-3 rounded-xl border transition-colors ${
                    selected
                      ? 'bg-dcp-primary/15 border-dcp-primary'
                      : 'bg-dcp-surface border-dcp-border hover:border-dcp-border-dark'
                  }`}
                >
                  <p
                    className={`font-semibold text-xs ${
                      selected ? 'text-dcp-primary' : 'text-dcp-text'
                    }`}
                  >
                    {dt.label}
                  </p>
                  <p className="text-dcp-text-3 text-[9px] truncate">{dt.hint}</p>
                </button>
              );
            })}
          </div>
        </LabeledField>

        <div className="h-3" />

        {form.displayType !== 'info_tile' && (
          <MediaField
            label="Görsel"
            kind="image"
            url={form.thumbnailUrl}
            publicId={form.thumbnailPublicId}
            onChange={({ url, publicId }) =>
              setForm((f) => ({ ...f, thumbnailUrl: url, thumbnailPublicId: publicId }))
            }
          />
        )}

        {/* Alan yalnizca KAYDEDILDIGI yerde gorunur. buildBody videoUrl'i
            sadece exercise_videos kategorisinde govdeye koyuyor; mobil tarafta
            ExploreItem.videoUrl'i okuyan tek ekran da ExerciseVideoDetailScreen
            ve o da yalnizca bu kategori icin aciliyor (explore_detail_screen).
            Kosul sadece displayType iken bir "training" icerigine yuklenen
            video Cloudinary'ye cikip govdeye hic girmiyor, yani dosya odenip
            sessizce atiliyordu. Alani her kategoride KAYDETMEK yerine sadece
            burada GOSTERMEYI sectik: kaydetmek hicbir ekranin okumadigi bir
            alan yazmak ve okunmayacak bir dosyayi kalici kilmak olurdu. */}
        {form.displayType === 'video' && form.category === 'exercise_videos' && (
          <MediaField
            label="Video"
            kind="video"
            url={form.videoUrl}
            publicId={form.videoPublicId}
            onChange={({ url, publicId }) =>
              setForm((f) => ({ ...f, videoUrl: url, videoPublicId: publicId }))
            }
          />
        )}

        <SectionLabel>Grid Ayarları</SectionLabel>
        <div className="flex gap-3">
          <div className="flex-1">
            <LabeledField label="Genişlik (spanX)" hint="Yatay kaplama">
              <Segmented options={[1, 2]} value={form.spanX} onChange={set('spanX')} />
            </LabeledField>
          </div>
          <div className="flex-1">
            <LabeledField label="Yükseklik (spanY)" hint="Dikey kaplama">
              <Segmented options={[1, 2]} value={form.spanY} onChange={set('spanY')} />
            </LabeledField>
          </div>
        </div>

        <div className="h-3" />

        <LabeledField label="Sıralama" hint="Yüksek değer = liste başında">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => set('order')(Math.max(0, form.order - 1))}
              className="w-9 h-9 rounded-full bg-dcp-surface-variant border border-dcp-border text-dcp-text"
            >
              −
            </button>
            <span className="text-dcp-text text-lg font-bold w-8 text-center">{form.order}</span>
            <button
              type="button"
              onClick={() => set('order')(form.order + 1)}
              className="w-9 h-9 rounded-full bg-dcp-surface-variant border border-dcp-border text-dcp-text"
            >
              +
            </button>
          </div>
        </LabeledField>

        <SectionLabel>Durum</SectionLabel>
        <div className="flex gap-3">
          <SwitchTile
            label="Aktif"
            hint="İçerik kullanıcılara görünür"
            value={form.isActive}
            onChange={set('isActive')}
          />
          <SwitchTile
            label="Premium"
            hint="Sadece premium kullanıcılar"
            value={form.isPremium}
            onChange={set('isPremium')}
          />
        </div>

        <hr className="border-dcp-border my-8" />

        {/* ── Kategoriye özel alanlar ── */}
        {form.category === 'recipes' && (
          <>
            <SectionLabel>Tarif Detayları</SectionLabel>
            <div className="flex gap-3">
              <div className="flex-1">
                <TextField label="Kalori" value={form.calories} onChange={set('calories')} type="number" />
              </div>
              <div className="flex-1">
                <TextField label="Hazırlık Süresi" value={form.prepTime} onChange={set('prepTime')} />
              </div>
            </div>
            <TextField
              label="Malzemeler (satır satır)"
              value={form.ingredients}
              onChange={set('ingredients')}
              rows={5}
            />
            <TextField label="Adımlar (satır satır)" value={form.steps} onChange={set('steps')} rows={5} />

            <SectionLabel>Makro Besinler</SectionLabel>
            <div className="flex gap-2">
              <div className="flex-1">
                <TextField label="Protein (g)" value={form.protein} onChange={set('protein')} type="number" />
              </div>
              <div className="flex-1">
                <TextField label="Karb (g)" value={form.carb} onChange={set('carb')} type="number" />
              </div>
              <div className="flex-1">
                <TextField label="Yağ (g)" value={form.fat} onChange={set('fat')} type="number" />
              </div>
            </div>
            <DifficultySelector value={form.difficulty} onChange={set('difficulty')} />
          </>
        )}

        {form.category === 'exercise_videos' && (
          <>
            <SectionLabel>Egzersiz Detayları</SectionLabel>
            <TextField label="Süre (örn: 5:00)" value={form.duration} onChange={set('duration')} />
            <LabeledField label="Hedef Kaslar" hint="Bu egzersizin çalıştırdığı kaslar">
              <div className="flex flex-wrap gap-2">
                {MUSCLES.map((muscle) => {
                  const selected = form.targetMuscles.includes(muscle.key);
                  return (
                    <button
                      key={muscle.key}
                      type="button"
                      onClick={() => toggleMuscle(muscle.key)}
                      className={`px-3.5 py-2 rounded-full border text-[13px] font-semibold transition-colors ${
                        selected
                          ? 'bg-dcp-primary border-dcp-primary text-dcp-on-primary'
                          : 'bg-dcp-surface border-dcp-border text-dcp-text-2 hover:border-dcp-border-dark'
                      }`}
                    >
                      {muscle.label}
                    </button>
                  );
                })}
              </div>
            </LabeledField>
            <div className="h-3" />
            <TextField
              label="Talimatlar (satır satır)"
              value={form.instructions}
              onChange={set('instructions')}
              rows={6}
            />
            <DifficultySelector value={form.difficulty} onChange={set('difficulty')} />
          </>
        )}

        {CONTENT_CATEGORIES.includes(form.category) && (
          <>
            <SectionLabel>İçerik Detayları</SectionLabel>
            <TextField
              label="İçerik (Markdown)"
              value={form.content}
              onChange={set('content')}
              rows={10}
            />
            <div className="flex gap-3">
              <div className="flex-1">
                <TextField label="Okuma Süresi" value={form.readTime} onChange={set('readTime')} />
              </div>
              <div className="flex-1">
                <TextField label="Etiketler (virgülle)" value={form.tags} onChange={set('tags')} />
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <TextField label="Puan (0-5)" value={form.rating} onChange={set('rating')} type="number" />
              </div>
              <div className="flex-1">
                <TextField
                  label="Puan Sayısı"
                  value={form.ratingCount}
                  onChange={set('ratingCount')}
                  type="number"
                />
              </div>
            </div>
            <TextField
              label="Öğrenme Hedefleri (satır satır)"
              value={form.learningObjectives}
              onChange={set('learningObjectives')}
              rows={4}
            />
          </>
        )}

        <button
          type="submit"
          disabled={isSaving}
          className="mt-6 w-full h-[50px] rounded-2xl bg-dcp-primary text-dcp-on-primary font-bold text-base disabled:bg-dcp-surface-variant disabled:text-dcp-text-3 transition-colors"
        >
          {isSaving ? 'Kaydediliyor…' : isEdit ? 'Kaydet' : 'Ekle'}
        </button>
      </div>
    </form>
  );
}

const DifficultySelector = ({ value, onChange }) => (
  <LabeledField label="Zorluk" hint="İçeriğin zorluk seviyesi">
    <div className="flex gap-2">
      {DIFFICULTIES.map((d) => {
        const selected = d.key === value;
        return (
          <button
            key={d.key}
            type="button"
            onClick={() => onChange(d.key)}
            className="flex-1 py-2.5 rounded-xl border font-semibold text-xs transition-colors"
            style={{
              backgroundColor: selected ? `${d.color}26` : '#1C1C1E',
              borderColor: selected ? d.color : '#2C2C2E',
              color: selected ? d.color : '#8E8E93',
            }}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  </LabeledField>
);
